import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useWeb3React } from '@web3-react/core';
import { metaMask } from '../config/connectors';
import { NETWORKS } from '../config/contracts';
import { toast } from 'react-toastify';

export interface WalletState {
  isConnecting: boolean;
  isConnected: boolean;
  account: string | null;
  chainId: number | null;
  balance: string;
  currentNetwork: typeof NETWORKS[keyof typeof NETWORKS] | null;
  supportedNetworks: typeof NETWORKS;
  error: string | null;
}

interface WalletContextType extends WalletState {
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  switchNetwork: (chainId: number) => Promise<void>;
  updateAccount: (account: string | null) => void;
  updateChainId: (chainId: number | null) => void;
  updateBalance: (balance: string) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

const WALLET_KEY = 'whitelist-admin-wallet';

const defaultWalletState: WalletState = {
  isConnecting: false,
  isConnected: false,
  account: null,
  chainId: null,
  balance: '0',
  currentNetwork: null,
  supportedNetworks: NETWORKS,
  error: null,
};

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const WalletProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { connector } = useWeb3React();
  const [wallet, setWallet] = useState<WalletState>(() => {
    try {
      const saved = localStorage.getItem(WALLET_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only restore persistent fields, other fields use default values
        const restoredState = {
          ...defaultWalletState,
          account: parsed.account || null,
          chainId: parsed.chainId || null,
          isConnected: parsed.isConnected || false,
        };
        // Set current network
        if (restoredState.chainId) {
          restoredState.currentNetwork = Object.values(NETWORKS).find(n => n.chainId === restoredState.chainId) || null;
        }
        return restoredState;
      }
      return defaultWalletState;
    } catch {
      return defaultWalletState;
    }
  });

  // Only save fields that need to be persisted
  useEffect(() => {
    try {
      const persistentData = {
        account: wallet.account,
        chainId: wallet.chainId,
        isConnected: wallet.isConnected,
      };
      localStorage.setItem(WALLET_KEY, JSON.stringify(persistentData));
      console.log('💾 Wallet status saved to localStorage:', persistentData);
    } catch (error) {
      console.warn('Failed to save wallet state:', error);
    }
  }, [wallet.account, wallet.chainId, wallet.isConnected]);

  const connect = useCallback(async () => {
    try {
      setWallet(prev => ({ ...prev, isConnecting: true, error: null }));
      await metaMask.activate();
      toast.success('Wallet connected successfully!');
    } catch (error: any) {
      console.error('Failed to connect wallet:', error);
      const errorMessage = error?.message || 'Failed to connect wallet';
      setWallet(prev => ({ ...prev, error: errorMessage }));
      toast.error(errorMessage);
    } finally {
      setWallet(prev => ({ ...prev, isConnecting: false }));
    }
  }, []);

  const reset = useCallback(() => {
    console.log('🔄 Wallet status reset');
    setWallet(defaultWalletState);
  }, []);

  const disconnect = useCallback(async () => {
    try {
      if (connector?.deactivate) {
        await connector.deactivate();
      } else {
        await connector?.resetState();
      }
      reset();
      toast.success('Wallet disconnected');
    } catch (error: any) {
      console.error('Failed to disconnect wallet:', error);
      const errorMessage = error?.message || 'Failed to disconnect wallet';
      setWallet(prev => ({ ...prev, error: errorMessage }));
      toast.error(errorMessage);
    }
  }, [connector, reset]);

  const switchNetwork = useCallback(async (targetChainId: number) => {
    try {
      setWallet(prev => ({ ...prev, error: null }));
      const targetNetwork = Object.values(NETWORKS).find(n => n.chainId === targetChainId);
      
      if (!targetNetwork) {
        throw new Error('Unsupported network');
      }

      if (window.ethereum) {
        await (window.ethereum as any).request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${targetChainId.toString(16)}` }],
        });
        toast.success(`Switched to ${targetNetwork.name}`);
      }
    } catch (error: any) {
      console.error('Failed to switch network:', error);
      const errorMessage = error?.message || 'Failed to switch network';
      setWallet(prev => ({ ...prev, error: errorMessage }));
      toast.error(errorMessage);
    }
  }, []);

  const updateAccount = useCallback((account: string | null) => {
    console.log('💾 updateAccount called:', { account, isConnected: !!account });
    setWallet(prev => ({
      ...prev,
      account,
      isConnected: !!account,
      error: null, // Clear error
    }));
  }, []);

  const updateChainId = useCallback((chainId: number | null) => {
    console.log('💾 updateChainId called:', { chainId });
    const currentNetwork = chainId ? Object.values(NETWORKS).find(n => n.chainId === chainId) || null : null;
    setWallet(prev => ({ ...prev, chainId, currentNetwork }));
  }, []);

  const updateBalance = useCallback((balance: string) => {
    setWallet(prev => ({ ...prev, balance }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setWallet(prev => ({ ...prev, error }));
  }, []);

  const clearError = useCallback(() => {
    setWallet(prev => ({ ...prev, error: null }));
  }, []);



  const value: WalletContextType = {
    ...wallet,
    connect,
    disconnect,
    switchNetwork,
    updateAccount,
    updateChainId,
    updateBalance,
    setError,
    clearError,
    reset,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = (): WalletContextType => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export default useWallet;